// apps/web/src/pages/admin/manual-create/ManualCreatePage.tsx — Phase 5 plan 05-04 (B3).
//
// Full-page manual creation form (NOT a dialog). On
// `OUTSIDE_SAME_DAY_CUTOFF` / `SLOT_FULL` with `forceFlagAvailable`,
// the page shows the inline warning + "Trotzdem eintragen" checkbox
// that re-submits with `forceFlag: true`.

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClientError } from "graphql-request";
import { useTranslation } from "react-i18next";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { gqlRequest } from "../../../lib/api";
import { useInvalidateAdminCaches } from "../../../lib/admin-cache";
import {
  CREATE_MANUAL_RESERVATION_MUTATION,
  RESTAURANT_MANUAL_QUERY,
  SLOT_DEFINITIONS_QUERY,
} from "./manual-create-operations";

function combineLocal(date: string, time: string, tz: string): string {
  // Wall-clock time in the restaurant timezone -> absolute UTC instant.
  // API contract (schema.ts CreateReservationInput): ISO 8601 in UTC;
  // server zod at manual-reservation.ts:94 requires
  // `.datetime({ offset: true })`.
  return fromZonedTime(`${date}T${time}:00`, tz).toISOString();
}

function validateDateParam(value: string | null): string | null {
  if (!value) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

export function ManualCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invalidate = useInvalidateAdminCaches();
  const initialSource =
    searchParams.get("source") === "vor_ort" ? "VOR_ORT" : "TELEFON";

  // 260803-ger D-06: the selected day flows in via ?date= so the
  // form initializes on the day the operator is currently editing.
  // Fall back to the Europe/Berlin wall-clock today (NOT UTC slice —
  // the previous UTC default drifted against the Europe/Berlin anchor
  // used by the rest of the admin surface near midnight).
  const queryDate = validateDateParam(searchParams.get("date"));
  const today = formatInTimeZone(new Date(), "Europe/Berlin", "yyyy-MM-dd");
  const initialDate = queryDate ?? today;

  const [source, setSource] = useState<"TELEFON" | "VOR_ORT">(initialSource);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState("18:00");
  const [partySize, setPartySize] = useState(2);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [forceConfirm, setForceConfirm] = useState(false);
  const [forceFlagAvailable, setForceFlagAvailable] = useState(false);
  const [warningMessageKey, setWarningMessageKey] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const restaurant = useQuery({
    queryKey: ["restaurant", "manual"],
    queryFn: async () => {
      const res = await gqlRequest<{
        restaurant: {
          id: string;
          minPartySize: number;
          maxPartySize: number;
          timezone: string;
        } | null;
      }>(RESTAURANT_MANUAL_QUERY);
      return res.restaurant;
    },
  });

  const slots = useQuery({
    queryKey: ["slotDefinitions"],
    queryFn: async () => {
      const res = await gqlRequest<{
        slotDefinitions: {
          id: string;
          startTime: string;
          durationMinutes: number;
        }[];
      }>(SLOT_DEFINITIONS_QUERY);
      return res.slotDefinitions;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (forceFlag: boolean) => {
      const matchedSlot = slots.data?.find((s) => s.startTime === time);
      if (!matchedSlot)
        throw new Error("No slot definition matches the selected time");
      const res = await gqlRequest<{
        createManualReservation: { reservationNumber: string };
      }>(CREATE_MANUAL_RESERVATION_MUTATION, {
        input: {
          source,
          serviceDate: date,
          slotDefinitionId: matchedSlot.id,
          startsAt: combineLocal(
            date,
            time,
            restaurant.data?.timezone ?? "Europe/Berlin",
          ),
          durationMinutes: matchedSlot.durationMinutes,
          partySize,
          name,
          email: email || null,
          phone: phone || null,
          internalNote: internalNote || null,
          forceFlag,
        },
      });
      return res.createManualReservation;
    },
    onSuccess: () => {
      invalidate();
      // 260803-ger D-06: post-create returns to the canonical
      // /admin/reservierungen surface (the merged Heute+Reservierungen
      // page) and preserves the selected date so the operator lands
      // back on the day they were editing.
      navigate(`/admin/reservierungen?date=${date}`);
    },
    onError: (err) => {
      if (err instanceof ClientError) {
        const ext = err.response.errors?.[0]?.extensions;
        const code = (ext as { code?: string } | undefined)?.code;
        const messageKey = (ext as { messageKey?: string } | undefined)
          ?.messageKey;
        const available = (ext as { forceFlagAvailable?: boolean } | undefined)
          ?.forceFlagAvailable;
        if (code === "OUTSIDE_SAME_DAY_CUTOFF" || code === "SLOT_FULL") {
          setForceFlagAvailable(!!available);
          setWarningMessageKey(messageKey ?? null);
          setError(null);
          return;
        }
      }
      setError(err instanceof Error ? err.message : "Error");
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (forceFlagAvailable && !forceConfirm) {
      setError(t("admin.manualCreate.forceConfirm"));
      return;
    }
    createMutation.mutate(forceFlagAvailable && forceConfirm);
  };

  return (
    <div className="px-4 py-6 max-w-2xl" data-testid="manual-create-page">
      <h1 className="font-heading text-2xl mb-4">
        {t("admin.manualCreate.pageTitle")}
      </h1>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
        data-testid="manual-create-form"
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm">
            {t("admin.manualCreate.sourceLabel")}
          </legend>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="source"
              value="TELEFON"
              checked={source === "TELEFON"}
              onChange={() => setSource("TELEFON")}
              data-testid="source-telefon"
            />
            <span>{t("admin.manualCreate.sourceTelefon")}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="source"
              value="VOR_ORT"
              checked={source === "VOR_ORT"}
              onChange={() => setSource("VOR_ORT")}
              data-testid="source-vorort"
            />
            <span>{t("admin.manualCreate.sourceVorOrt")}</span>
          </label>
        </fieldset>
        <label className="flex flex-col gap-1">
          <span className="text-sm">{t("admin.manualCreate.dateLabel")}</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="field"
            data-testid="manual-date"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">{t("admin.manualCreate.timeLabel")}</span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            required
            className="field"
            data-testid="manual-time"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">
            {t("admin.manualCreate.partySizeLabel")}
          </span>
          <input
            type="number"
            min={restaurant.data?.minPartySize ?? 1}
            max={restaurant.data?.maxPartySize ?? 20}
            value={partySize}
            onChange={(e) => setPartySize(Number(e.target.value))}
            required
            className="field"
            data-testid="manual-party-size"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">{t("admin.manualCreate.nameLabel")}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="field"
            data-testid="manual-name"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">
            {t("admin.manualCreate.emailOptional")}
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field"
            data-testid="manual-email"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">
            {t("admin.manualCreate.phoneOptional")}
          </span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="field"
            data-testid="manual-phone"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">
            {t("admin.manualCreate.internalNoteLabel")}
          </span>
          <textarea
            maxLength={2000}
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            className="field"
            data-testid="manual-internal-note"
          />
        </label>

        {forceFlagAvailable && warningMessageKey && (
          <div
            className="card p-4 border-error"
            role="alert"
            data-testid="force-flag-warning"
          >
            <p className="text-sm mb-2">
              {warningMessageKey === "admin.manualCreate.capacityExceeded"
                ? t("admin.manualCreate.capacityWarning")
                : t("admin.manualCreate.cutoffWarning")}
            </p>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={forceConfirm}
                onChange={(e) => setForceConfirm(e.target.checked)}
                data-testid="force-flag-confirm"
              />
              <span className="text-sm">
                {t("admin.manualCreate.forceConfirm")}
              </span>
            </label>
          </div>
        )}

        {error && (
          <p
            role="alert"
            className="text-sm text-error"
            data-testid="manual-error"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={createMutation.isPending}
          className="btn btn-primary"
          data-testid="manual-submit"
        >
          {t("admin.manualCreate.submit")}
        </button>
      </form>
    </div>
  );
}
